import { useEffect, useRef, forwardRef, useMemo, useState, useCallback } from 'react'
import { Row, Col, Popover, Table, Input, Button, Checkbox, Dropdown, Menu, MenuProps } from 'antd'
import { calculateColumnWidthBasedOnText, renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import MenuIcon from 'Components/Svg/MenuIcon'
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  SaveFilled,
  CloseOutlined,
  CheckOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { animated } from 'react-spring'
import { columnCloseWidth } from 'Constants/TableConfiguration'
import { getAnswers, getSettings } from 'Services/Settings/SettingsService'
import CountryFlag from 'react-country-flag'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { getActiveFilterValue } from 'Helpers/FilterHelper'
import { saveDescriptonChanges } from 'Services/Kiosk/DescriptionServices'
import DescriptionModal from 'Components/Kiosk/DescriptionModal'
import { useOutletContext } from 'react-router-dom'
import Popconfirm from 'antd/es/popconfirm'

function Descriptions({ style, className, onMouseDown, onMouseUp, onTouchEnd, children, ...props }, ref) {
  const dispatch = useAppDispatch()
  const descriptionsDatabase = useAppSelector(state => state.present.articleDetail.articleSelected.descriptions)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const workspaceImagesVisible = useAppSelector(state => state.present.configuration.workspaceImagesVisible)
  const language = useAppSelector(state => state.present.language.value)
  const answers = useAppSelector(state => state.present.settings.answers)
  const [descriptionsFilters, setdescriptionsFilters] = useState([])
  const [groupSet, setGroupSet] = useState([])
  const [changedDescription, setChangedDescription] = useState([])
  const [activeSaveButton, setActiveSaveButton] = useState(false)
  const [descriptionErrors, setdescriptionErrors] = useState([])
  const [isColumnOpen, setIsColumnOpen] = useState([])
  const [componentWidth, setComponentWidth] = useState(0)
  const tableRef = useRef<any>(undefined)
  const [checkAll, setCheckAll] = useState(false)
  const [indeterminate, setIndeterminate] = useState(true)
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [descriptionsCheckboxes, setHandledCheckboxes] = useState(JSON.parse(JSON.stringify('')))
  const bodyrefContext = useOutletContext() as React.MutableRefObject<undefined>

  useEffect(() => {
    dispatch(getAnswers('DescriptionsAndSeoSettings'))
  }, [])

  useEffect(() => {
    const lang = answers?.DescriptionsAndSeoSettings?.rawAnswers?.filter(
      item => item.settingsName == 'DescriptionsAndSeoSettings' && item.questionCode == 'Languages',
    )
    const set = answers?.DescriptionsAndSeoSettings?.respondingEntities.filter(f =>
      lang.some(e => e.respondingEntityId == f.id),
    )
    setGroupSet(set)
    setIsColumnOpen(set)
  }, [answers])

  useEffect(() => {
    setTimeout(() => {
      setComponentWidth(
        (tableRef?.current?.querySelector('.ant-table-container .ant-table-thead') as HTMLElement)?.offsetWidth + 1,
      )
    }, 100)
  }, [groupSet])

  useMemo(() => {
    setdescriptionsFilters(groupSet?.map(item => ({ ...item, active: true, visible: true })))
    if (descriptionsFilters) setHandledCheckboxes(JSON.parse(JSON.stringify(descriptionsFilters)))
  }, [groupSet])

  const getIsoLanguagueFromId = id => {
    return answers?.DescriptionsAndSeoSettings?.questionGroup?.questions.find(e => e.code === 'Languages')
      ?.possibleValues[id]?.extraFields?.ISO
  }

  const storedChange = (value, id, lang, set) => {
    setChangedDescription(prevSavedDescription => {
      const existingIndex = prevSavedDescription.findIndex(
        item => item.id === id && item.set === set && item.lang === lang,
      )
      return existingIndex !== -1
        ? [
            ...prevSavedDescription.slice(0, existingIndex),
            { set: set, id: id, lang: lang, value: value },
            ...prevSavedDescription.slice(existingIndex + 1),
          ]
        : [...prevSavedDescription, { set: set, id: id, lang: lang, value: value }]
    })
    setActiveSaveButton(true)
  }

  const filterDBbyTitle = (id, valueDB, lang, set) => {
    return Object.entries(valueDB)
      .filter(([k, v]) => v['lang'] == lang && v['set'] == set && v['id'] == id)
      .map(([k, v]) => v['value'])[0]
  }

  const DescriptionInput = ({ id, title, lang, settings, set, valueDB }) => {
    const html = settings.filter(f => f.questionCode == id + 'TypeOfDescriptionField')?.map(f => f.answer)[0] == 1
    const maxchars = settings.filter(f => f.questionCode == id + 'MaxCharacters')?.map(f => f.answer)[0]
    const minchars = settings.filter(f => f.questionCode == id + 'MinCharacters')?.map(f => f.answer)[0]
    const isvisible =
      settings
        .filter(f => f.questionCode == id + 'IsVisible')
        ?.map(f => f.answer)[0]
        .toLowerCase() == 'true'
    const changed = changedDescription?.filter(f => f.set == set && f.id == id && f.lang == lang)?.map(e => e.value)[0]
    const dbvalue = filterDBbyTitle(id, valueDB, lang, set)
    const [text, setText] = useState(changed ? changed : dbvalue)
    const [oldtext, setOldText] = useState(dbvalue)
    const idInput = set + '_' + id + '_' + lang

    const handleBlur = e => {
      const value = html ? e : e.target.value
      if (text != oldtext) {
        storedChange(value, id, lang, set)
        setOldText(value)
      }
    }
    const handleChange = e => {
      const value = html ? e : e.target.value
      setActiveSaveButton(true)
      setText(value)
    }

    if (!isvisible) return

    if (html) {
      return (
        <div className='pl-2 pr-2 pb-5 div-textarea'>
          <label className='label-textarea'>{title}</label>{' '}
          <div>
            <div>
              <ReactQuill
                id={idInput}
                style={{ width: 350, background: 'white', textAlign: 'left' }}
                value={text}
                onBlur={(range, source, editor) => {
                  handleBlur(editor.getHTML())
                }}
                onChange={handleChange}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link'],
                  ],
                }}
                formats={['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'image']}
              />
            </div>
            <div>
              <p className='minchars'> Minchars: {minchars} </p>
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className='pl-2 pr-2  div-textarea'>
          <label className='label-textarea'>{title}</label>
          <Input.TextArea
            id={idInput}
            value={text}
            onBlur={handleBlur}
            onChange={handleChange}
            showCount
            maxLength={maxchars}
            style={{ width: 350, height: 60, resize: 'none' }}
          />
          <p className='minchars'> Minchars: {minchars} </p>
        </div>
      )
    }
  }

  const handleCopySetIn = (setId: string, checklist: any[]) => {
    const formTextareas = document.querySelectorAll('textarea[id^="' + setId + '"]') as NodeListOf<HTMLTextAreaElement>
    formTextareas.forEach(textarea => {
      checklist.map(setNew => {
        const textareaid = textarea.id.replace(setId, setNew)
        const toTextareas = document.querySelectorAll(
          'textarea[id="' + textareaid + '"]',
        ) as NodeListOf<HTMLTextAreaElement>
        toTextareas.forEach(textareanew => {
          textareanew.value = textarea.value
          storedChange(textarea.value, textarea.id.split('_')[1], textarea.id.split('_')[2], setNew)
        })
      })
    })
  }
  const handleCopySetAll = (setId: string) => {
    const formTextareas = document.querySelectorAll('textarea[id^="' + setId + '"]') as NodeListOf<HTMLTextAreaElement>
    formTextareas.forEach(textarea => {
      groupSet
        ?.filter(f => f.id != setId)
        .map(e => {
          const textareaid = textarea.id.replace(setId, e.id)
          const toTextareas = document.querySelectorAll(
            'textarea[id="' + textareaid + '"]',
          ) as NodeListOf<HTMLTextAreaElement>
          toTextareas.forEach(textareanew => {
            textareanew.value = textarea.value
            storedChange(textarea.value, textarea.id.split('_')[1], textarea.id.split('_')[2], e.id)
          })
        })
    })
  }

  const setItems = (setId: string): MenuProps['items'] => {
    let checked = []
    let groupfilter = []
    for (const setnew of groupSet.filter(f => f.id !== setId)) {
      const formTextareas = document.querySelectorAll(
        'textarea[id^="' + setId + '"]',
      ) as NodeListOf<HTMLTextAreaElement>

      for (const textarea of formTextareas) {
        const textareaid = textarea.id.replace(setId, setnew.id)
        const foundtextarea = document.querySelectorAll('textarea[id="' + textareaid + '"]')
        if (foundtextarea && foundtextarea.length > 0) {
          groupfilter.push({
            id: setnew.id,
            desc: setnew.name,
          })
          break
        }
      }
    }
    return [
      {
        key: setId + '_1',
        label: (
          <Popconfirm
            title={
              <>
                <div>{renderTranslation(language, TranslationKeys.copyselectedset)}</div>
                <Checkbox.Group
                  onChange={checkedValues => {
                    checked = checkedValues
                  }}
                >
                  {groupfilter.map(set => (
                    <Checkbox id={setId} value={set.id} key={setId + '-' + set.id + '-checkbox'}>
                      {set.desc}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </>
            }
            onConfirm={e => handleCopySetIn(setId, checked)}
            onCancel={stopPropagation}
            okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
            cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
            placement='bottom'
          >
            <div onClick={stopPropagation}>{renderTranslation(language, TranslationKeys.copyin)}</div>
          </Popconfirm>
        ),
      },
      {
        key: setId + '_2',
        label: (
          <Popconfirm
            title={renderTranslation(language, TranslationKeys.copyfullset)}
            onConfirm={e => handleCopySetAll(setId)}
            onCancel={stopPropagation}
            okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
            cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
            placement='bottom'
          >
            <div onClick={stopPropagation}>{renderTranslation(language, TranslationKeys.copyinallset)}</div>
          </Popconfirm>
        ),
      },
    ]
  }

  const setTableColumns = useMemo(() => {
    let groupkey = 0
    return groupSet?.map(e => {
      return {
        title: (
          <div className='setdesc-group-table-head-box' title={e.name}>
            {isColumnOpen.some(f => f.id == e.id) ? (
              <div style={{ width: '100%', display: 'inline-flex' }}>
                <div style={{ paddingLeft: 15, paddingTop: 5 }}>
                  <Dropdown autoAdjustOverflow={true} menu={{ items: setItems(e.id) }} placement='bottomLeft'>
                    <MoreOutlined className={'dots-icon d-inline-flex'} />
                  </Dropdown>
                </div>
                <div style={{ width: '90%' }}>
                  <CaretLeftOutlined
                    className='multi-table-collapse-icon'
                    onClick={() => {
                      setIsColumnOpen(
                        isColumnOpen.filter(function (old) {
                          return old.id != e.id
                        }),
                      )
                    }}
                  />
                  <span className={props.editDisabled ? '' : 'text-underline cursor-pointer'}> {e.name} </span>
                </div>
              </div>
            ) : (
              <>
                <span>
                  <CaretRightOutlined
                    className='multi-table-collapse-icon'
                    title={e.name}
                    onClick={() => {
                      setIsColumnOpen([...isColumnOpen, e])
                    }}
                  />
                </span>
              </>
            )}
          </div>
        ),
        dataIndex: 'setGroup-' + e.id + '-' + (groupkey += 1),
        key: 'setGroup-' + e.id + '-' + (groupkey += 1),
        children: answers?.DescriptionsAndSeoSettings?.rawAnswers
          ?.filter(
            item =>
              item.respondingEntityId === e.id &&
              item.settingsName == 'DescriptionsAndSeoSettings' &&
              item.questionCode == 'Languages',
          )
          .map(a => {
            return {
              dataIndex: 'setGroupChild-' + e.id + '-' + (groupkey += 1),
              key: 'setGroupChild-' + e.id + '-' + (groupkey += 1),
              children: [
                {
                  dataIndex: 'setGroupChild2-' + e.id + '-' + a.answer + '-' + (groupkey += 1),
                  key: 'setGroupChild2-' + e.id + '-' + a.answer + '-' + (groupkey += 1),
                  width: calculateColumnWidthBasedOnText(a.answer),
                  render: (text, record, index) => {
                    return isColumnOpen.some(f => f.id == e.id) ? (
                      <div style={{ background: '#eff2f5' }}>
                        <div
                          className='text-right setdesc-group-sector-head-box'
                          title={getIsoLanguagueFromId(a.answer)}
                        >
                          <span className={props.editDisabled ? '' : 'text-underline cursor-pointer'}>
                            <CountryFlag
                              countryCode={
                                getIsoLanguagueFromId(a.answer) === 'EN' ? 'GB' : getIsoLanguagueFromId(a.answer)
                              }
                              svg
                              style={{ width: '2em', height: '2em' }}
                            />
                          </span>
                        </div>
                        <div>
                          {answers?.DescriptionsAndSeoSettings?.questionGroup?.questionGroups?.map(q => {
                            return (
                              <DescriptionInput
                                id={q.id}
                                title={q.name}
                                settings={answers?.DescriptionsAndSeoSettings?.rawAnswers?.filter(
                                  item =>
                                    item.respondingEntityId === e.id &&
                                    item.settingsName == 'DescriptionsAndSeoSettings' &&
                                    item.questionCode.startsWith(q.id),
                                )}
                                lang={a.answer}
                                set={e.id}
                                valueDB={descriptionsDatabase}
                                key={a.answer + e.id + q.name + '-descriptionInput' + '-' + (groupkey += 1)}
                              />
                            )
                          })}
                        </div>
                      </div>
                    ) : null
                  },
                },
              ],
            }
          }),
      }
    })
  }, [isColumnOpen, props.editDisabled, groupSet, answers, descriptionsDatabase, changedDescription])

  const onCheckAllChange = useCallback(
    e => {
      const currentFilters = JSON.parse(JSON.stringify(descriptionsCheckboxes))
      currentFilters.forEach(element => {
        element.active = e.target.checked
      })

      setHandledCheckboxes(currentFilters)
      setIndeterminate(!e.target.checked ? true : false)
      setCheckAll(e.target.checked)
    },
    [descriptionsCheckboxes],
  )

  const onChangeCheckbox = useCallback(
    checkedValues => {
      const currentFilters = JSON.parse(JSON.stringify(descriptionsCheckboxes))

      currentFilters.forEach((element, index) => {
        if (checkedValues.indexOf(element.value) !== -1) {
          element.active = true
        } else {
          element.active = false
        }
      })
      setHandledCheckboxes(currentFilters)

      setIndeterminate(!!checkedValues.length && checkedValues.length < currentFilters.length)
      setCheckAll(checkedValues.length === currentFilters.length)
    },
    [descriptionsCheckboxes],
  )
  const handleFilterVisibility = useCallback(
    visible => {
      if (!editTableDisable) {
        return
      }
      if (!visible) {
        setHandledCheckboxes(JSON.parse(JSON.stringify(descriptionsFilters)))
      }
      setPopoverVisible(visible)
    },
    [descriptionsFilters, editTableDisable],
  )

  const saveChanges = e => {
    setdescriptionErrors([])
    let errcount = 0
    changedDescription.map(c => {
      const set = answers?.DescriptionsAndSeoSettings?.respondingEntities
        .filter(f => f.id == c.set)
        ?.map(f => [f.id, f.name])[0]
      const setid = set[0]
      const setdesc = set[1]
      const listcheck = answers?.DescriptionsAndSeoSettings?.rawAnswers?.filter(
        item =>
          item.respondingEntityId === setid &&
          item.settingsName === 'DescriptionsAndSeoSettings' &&
          item.questionCode.startsWith(c.id),
      )
      const questkey = c.id + 'MinCharacters'
      const minchars = listcheck.filter(f => f.questionCode.toLowerCase() === questkey)?.map(f => parseInt(f.answer))[0]
      const ismandatory =
        setid ??
        listcheck ??
        listcheck
          .filter(f => f.questionCode == c.id + 'IsMandatory')
          ?.map(f => f.answer)[0]
          .toLowerCase() == 'true'

      if (c.value.length > 0 && c.value.length < parseInt(minchars)) {
        errcount += 1
        setdescriptionErrors(pre => {
          return [
            ...pre,
            {
              set: c.set,
              id: c.id,
              lang: getIsoLanguagueFromId(c.lang),
              error:
                setdesc + ' ' + c.id + ' ' + getIsoLanguagueFromId(c.lang) + ' MinCharacters ' + parseInt(minchars),
            },
          ]
        })
      }

      if (c.value.length === 0 && ismandatory) {
        errcount += 1
        setdescriptionErrors(pre => {
          return [
            ...pre,
            {
              set: c.set,
              id: c.id,
              lang: getIsoLanguagueFromId(c.lang),
              error: setdesc + ' ' + c.id + ' ' + getIsoLanguagueFromId(c.lang) + ' is Mandatory',
            },
          ]
        })
      }
    })

    answers?.DescriptionsAndSeoSettings?.questionGroup?.questionGroups?.map(title => {
      answers?.DescriptionsAndSeoSettings?.respondingEntities.map(set => {
        const ismandatory =
          answers?.DescriptionsAndSeoSettings?.rawAnswers
            ?.filter(
              item =>
                item.respondingEntityId === set.id &&
                item.settingsName == 'DescriptionsAndSeoSettings' &&
                item.questionCode == title.id + 'IsMandatory',
            )
            ?.map(f => f.answer)[0]
            .toLowerCase() == 'true'

        const db =
          Object.entries(descriptionsDatabase).filter(
            ([k, v]) => v['set'] == set.id && v['id'] == title.id && v['value'] !== '',
          ).length > 0
        const mod = changedDescription.filter(f => f.set == set.id && f.id == title.id).length > 0

        if (ismandatory && !mod && !db) {
          setdescriptionErrors(pre => {
            return [
              ...pre,
              {
                set: set.id,
                id: title.name,
                lang: null,
                error: set.name + ' ' + title.name + ' ' + renderTranslation(language, TranslationKeys.ismandatory),
              },
            ]
          })
          errcount += 1
        }
      })
    })

    if (errcount == 0)
      dispatch(saveDescriptonChanges(changedDescription)).finally(() => {
        setChangedDescription([])
        setActiveSaveButton(false)
      })
  }

  const saveDescriptionsFilter = useCallback(() => {
    setPopoverVisible(false)
    //dispatch(saveFilter(JSON.parse(JSON.stringify(descriptionsCheckboxes))))
  }, [dispatch, descriptionsCheckboxes])

  const closeDescriptionsFilter = useCallback(() => {
    setHandledCheckboxes(JSON.parse(JSON.stringify(descriptionsFilters)))
    setPopoverVisible(false)
  }, [descriptionsFilters])

  const handlingCheckboxContent = useMemo(() => {
    return (
      <>
        <Checkbox
          className='descriptions-checkbox select-all general-checkbox'
          indeterminate={indeterminate}
          onChange={onCheckAllChange}
          checked={checkAll}
        >
          {renderTranslation(language, TranslationKeys.checkboxSelectDeselectAllLabel)}
        </Checkbox>
        <Checkbox.Group
          className='descriptions-checkbox-container'
          onChange={checkedValues => {
            onChangeCheckbox(checkedValues)
          }}
          value={getActiveFilterValue(descriptionsCheckboxes)}
        >
          <Row onClick={stopPropagation}>
            {descriptionsFilters?.map(e => {
              return e.visibile ? (
                <Col key={e.name} span={24}>
                  <Checkbox className={'descriptions-checkbox general-checkbox'} value={e.id}>
                    {e.name}
                  </Checkbox>
                </Col>
              ) : null
            })}
          </Row>
        </Checkbox.Group>
      </>
    )
  }, [groupSet, indeterminate, onCheckAllChange, checkAll, language, onChangeCheckbox, descriptionsCheckboxes])

  const handlingCheckboxTitle = useMemo(
    () => (
      <Row
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <Col span={24} className='descriptions-checkbox-title'>
          <div className='descriptions-checkbox-title-head'>
            {renderTranslation(language, TranslationKeys.quantitiesCheckboxTitle)}
            <span className='float-right'>
              <CheckOutlined
                className='descriptions-check-icon general-check-icon'
                onClick={e => {
                  e.stopPropagation()
                  saveDescriptionsFilter()
                }}
              />
              <CloseOutlined
                className='descriptions-close-icon general-clear-icon'
                onClick={e => {
                  e.stopPropagation()
                  closeDescriptionsFilter()
                }}
              />
            </span>
          </div>
        </Col>
      </Row>
    ),
    [language, saveDescriptionsFilter, closeDescriptionsFilter],
  )
  return workspaceImagesVisible && groupSet && groupSet.length > 0 ? (
    <Row>
      <Col className='pt-2 pl-2 pr-2' span={24}>
        <Row className='w-100'>
          <Col>
            <Popover
              id='descriptionsCheckboxContent'
              title={handlingCheckboxTitle}
              content={handlingCheckboxContent}
              placement='bottomLeft'
              autoAdjustOverflow={true}
              trigger='click'
              open={popoverVisible}
              onOpenChange={open => {
                handleFilterVisibility(open)
              }}
            >
              <MenuIcon
                onClick={stopPropagation}
                className={'generic-icon menu-icon' + (!editTableDisable ? ' disable' : '')}
              />
            </Popover>
          </Col>
          <Col>
            <SaveFilled
              className={'generic-icon save-icon' + (activeSaveButton ? ' active' : '')}
              onClick={saveChanges}
            />
            <DescriptionModal bodyref={bodyrefContext} errors={descriptionErrors} />
          </Col>
        </Row>
        <Row className='pt-4 w-100' ref={tableRef}>
          <Col className='overflow-x-auto' span={24}>
            <animated.div
              style={{
                width: isColumnOpen && componentWidth ? componentWidth : columnCloseWidth,
                transition: 'width 0.6s ease',
              }}
              className={'setdesc multi-table-slide-container' + (isColumnOpen ? ' open' : ' closed')}
            >
              <Table
                className={'discount-setdesc-group-table multi-table same-table-scroll'}
                columns={setTableColumns}
                dataSource={[groupSet.map(item => ({ key: item.name }))[0]]}
                pagination={false}
              />
            </animated.div>
          </Col>
        </Row>
      </Col>
    </Row>
  ) : null
}

export default forwardRef(Descriptions)
